// Bulk project scheduling — one template, many clients (Financial Cents "For Many Clients").
// Step 1: template + clients (search / client group / team member). Step 2: shared fields. Server: POST /api/projects/bulk.
import { useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, fetchWithAuth, Account, ProjectTemplate, User } from "@/lib/api";
import { useAuth } from "@/lib/authContext";
import { useToast } from "@/hooks/use-toast";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2 } from "lucide-react";
import { RECURRENCE_OPTIONS } from "@/lib/recurrenceOptions";

const MONTHS = ["01", "02", "03", "04", "05", "06", "07", "08", "09", "10", "11", "12"];
const MONTH_NAMES = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

export function BulkCreateProjectsDialog({ open, onOpenChange }: { open: boolean; onOpenChange: (o: boolean) => void }) {
  const { user } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const [step, setStep] = useState<1 | 2>(1);
  const [templateId, setTemplateId] = useState("");
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [search, setSearch] = useState("");
  const [groupId, setGroupId] = useState("none");
  const [memberId, setMemberId] = useState("none");
  const [f, setF] = useState({ title: "", start_date: "", due_date: "", internal_due_date: "", recurring_pattern: "", accounting_period_date: "", tax_year: "", period_label: "" });
  const [fillMe, setFillMe] = useState(true);
  const [saving, setSaving] = useState(false);

  const { data: templates = [] } = useQuery<ProjectTemplate[]>({ queryKey: ["project-templates"], queryFn: () => api.getProjectTemplates().catch(() => []), enabled: open });
  const { data: accounts = [] } = useQuery<Account[]>({ queryKey: ["accounts"], queryFn: () => api.getAccounts(), enabled: open });
  const { data: groups = [] } = useQuery<{ id: string; name: string }[]>({ queryKey: ["client-groups"], queryFn: () => fetchWithAuth("/api/client-groups"), enabled: open });
  const { data: users = [] } = useQuery<User[]>({ queryKey: ["users", "assignable"], queryFn: () => api.getUsers({ assignable: true }), enabled: open });
  const { data: members = [] } = useQuery({ queryKey: ["account-members-bulk"], queryFn: () => api.getAllAccountMembers(), enabled: open });

  const activeAccounts = useMemo(
    () => accounts.filter((a) => a.id && a.is_active !== false).sort((a, b) => a.account_name.localeCompare(b.account_name)),
    [accounts],
  );
  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    return q ? activeAccounts.filter((a) => a.account_name.toLowerCase().includes(q)) : activeAccounts;
  }, [activeAccounts, search]);

  const addIds = (ids: string[]) => setSelected((prev) => { const n = new Set(prev); ids.forEach((id) => n.add(id)); return n; });
  const toggle = (id: string) => setSelected((prev) => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const activeIds = useMemo(() => new Set(activeAccounts.map((a) => a.id)), [activeAccounts]);

  const pickGroup = async (id: string) => {
    setGroupId(id);
    if (id === "none") return;
    try {
      const rows = await fetchWithAuth<{ account_id: string }[]>(`/api/client-groups/${id}/members`);
      addIds(rows.map((r) => r.account_id).filter((a) => activeIds.has(a)));
    } catch (e) {
      toast({ title: "Couldn't load group", description: (e as Error).message, variant: "destructive" });
    }
  };
  const pickMember = (id: string) => {
    setMemberId(id);
    if (id !== "none") addIds(members.filter((m) => m.user_id === id && activeIds.has(m.account_id)).map((m) => m.account_id));
  };

  const allVisibleChecked = visible.length > 0 && visible.every((a) => selected.has(a.id));
  const reset = () => {
    setStep(1); setTemplateId(""); setSelected(new Set()); setSearch(""); setGroupId("none"); setMemberId("none");
    setF({ title: "", start_date: "", due_date: "", internal_due_date: "", recurring_pattern: "", accounting_period_date: "", tax_year: "", period_label: "" });
    setFillMe(true);
  };

  const submit = async () => {
    setSaving(true);
    try {
      const r = await api.bulkCreateProjects({
        account_ids: [...selected],
        template_id: templateId,
        title: f.title.trim(),
        start_date: f.start_date || null,
        due_date: f.due_date || null,
        internal_due_date: f.internal_due_date || null,
        recurring_pattern: f.recurring_pattern || null,
        accounting_period_date: f.accounting_period_date || null,
        tax_year: f.tax_year ? Number(f.tax_year) : null,
        period_label: f.period_label || null,
        fill_unassigned_user_id: fillMe ? user?.id : null,
      });
      const name = (id: string) => accounts.find((a) => a.id === id)?.account_name ?? id;
      const parts = [`${r.created} created`];
      if (r.skipped.length) parts.push(`${r.skipped.length} skipped (already open): ${r.skipped.map(name).join(", ")}`);
      if (r.failed.length) parts.push(`${r.failed.length} failed: ${r.failed.map((x) => name(x.account_id)).join(", ")}`);
      toast({ title: "Bulk create finished", description: parts.join(" · "), variant: r.failed.length ? "destructive" : undefined });
      queryClient.invalidateQueries({ queryKey: ["projects"] });
      reset();
      onOpenChange(false);
    } catch (e) {
      toast({ title: "Bulk create failed", description: (e as Error).message, variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!saving) { if (!o) reset(); onOpenChange(o); } }}>
      <DialogContent className="max-w-2xl max-h-[90vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>Create project for many clients</DialogTitle>
          <DialogDescription>
            {step === 1
              ? "Pick a template, then choose clients individually, by client group, or by the team member assigned to them."
              : `Creating "${f.title}" for ${selected.size} client${selected.size === 1 ? "" : "s"}. These values apply to every client.`}
          </DialogDescription>
        </DialogHeader>

        {step === 1 ? (
          <div className="space-y-3 overflow-hidden flex flex-col min-h-0">
            <div className="space-y-1">
              <Label>Template</Label>
              <Select value={templateId} onValueChange={(v) => { setTemplateId(v); setF((p) => ({ ...p, title: templates.find((t) => t.id === v)?.title ?? p.title })); }}>
                <SelectTrigger><SelectValue placeholder="Select a template..." /></SelectTrigger>
                <SelectContent>
                  {[...templates].sort((a, b) => a.title.localeCompare(b.title)).map((t) => <SelectItem key={t.id} value={t.id}>{t.title}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-3 gap-2">
              <Input placeholder="Search clients" value={search} onChange={(e) => setSearch(e.target.value)} />
              <Select value={groupId} onValueChange={pickGroup}>
                <SelectTrigger><SelectValue placeholder="Add a client group" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Add a client group…</SelectItem>
                  {groups.map((g) => <SelectItem key={g.id} value={g.id}>{g.name}</SelectItem>)}
                </SelectContent>
              </Select>
              <Select value={memberId} onValueChange={pickMember}>
                <SelectTrigger><SelectValue placeholder="Add by team member" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Add by team member…</SelectItem>
                  {users.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center justify-between text-sm">
              <label className="flex items-center gap-2 cursor-pointer">
                <Checkbox
                  checked={allVisibleChecked}
                  onCheckedChange={(c) => setSelected((prev) => { const n = new Set(prev); visible.forEach((a) => (c ? n.add(a.id) : n.delete(a.id))); return n; })}
                />
                Select all shown ({visible.length})
              </label>
              <span className="text-muted-foreground">
                {selected.size} selected{selected.size > 0 && <button className="ml-2 underline" onClick={() => setSelected(new Set())}>clear</button>}
              </span>
            </div>
            <div className="border rounded-md overflow-y-auto min-h-0 max-h-[45vh]">
              {visible.map((a) => (
                <label key={a.id} className="flex items-center gap-3 px-3 py-2 border-b last:border-b-0 hover:bg-muted/40 cursor-pointer text-sm">
                  <Checkbox checked={selected.has(a.id)} onCheckedChange={() => toggle(a.id)} />
                  {a.account_name}
                </label>
              ))}
              {visible.length === 0 && <p className="p-4 text-sm text-muted-foreground">No matching clients.</p>}
            </div>
          </div>
        ) : (
          <div className="space-y-3 overflow-y-auto">
            <div className="space-y-1">
              <Label>Project name <span className="text-destructive">*</span></Label>
              <Input value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} />
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div className="space-y-1"><Label>Start date</Label><Input type="date" value={f.start_date} onChange={(e) => setF({ ...f, start_date: e.target.value })} /></div>
              <div className="space-y-1"><Label>Client due date</Label><Input type="date" value={f.due_date} onChange={(e) => setF({ ...f, due_date: e.target.value })} /></div>
              <div className="space-y-1"><Label>Internal due</Label><Input type="date" value={f.internal_due_date} onChange={(e) => setF({ ...f, internal_due_date: e.target.value })} /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label>Recurrence</Label>
                <Select value={f.recurring_pattern || "none"} onValueChange={(v) => setF({ ...f, recurring_pattern: v === "none" ? "" : v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {RECURRENCE_OPTIONS.map((o) => (
                      <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {f.recurring_pattern && !f.due_date && <p className="text-xs text-amber-700">Set a due date so the next occurrence can be scheduled.</p>}
              </div>
              <div className="space-y-1">
                <Label>Accounting period date</Label>
                <Input type="date" value={f.accounting_period_date} onChange={(e) => setF({ ...f, accounting_period_date: e.target.value })} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label>Year</Label>
                <Select value={f.tax_year || "none"} onValueChange={(v) => setF({ ...f, tax_year: v === "none" ? "" : v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">— None —</SelectItem>
                    {[2027, 2026, 2025, 2024].map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label>Month</Label>
                <Select value={f.period_label || "none"} onValueChange={(v) => setF({ ...f, period_label: v === "none" ? "" : v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">— None —</SelectItem>
                    {MONTHS.map((m, i) => <SelectItem key={m} value={m}>{MONTH_NAMES[i]}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <Checkbox checked={fillMe} onCheckedChange={(c) => setFillMe(!!c)} />
              Automatically add me ({user?.name ?? "me"}) to tasks the template leaves unassigned
            </label>
          </div>
        )}

        <DialogFooter>
          {step === 2 && <Button variant="outline" onClick={() => setStep(1)} disabled={saving}>Back</Button>}
          {step === 1 ? (
            <Button onClick={() => setStep(2)} disabled={!templateId || selected.size === 0}>Next ({selected.size})</Button>
          ) : (
            <Button onClick={submit} disabled={saving || !f.title.trim()}>
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              {saving ? `Creating ${selected.size}…` : `Create ${selected.size} project${selected.size === 1 ? "" : "s"}`}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
